"use client";
import React, { useState, useEffect } from 'react';
import styles from './BlogDisclaimerComponent.module.css';
import "../../src/common/grid.css";
import { useTranslation } from 'react-i18next';
import { submitDownloadEbookForm } from '../../src/services/api'; // Import the API function

const Disclaimer = () => {
  const { t } = useTranslation();
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone: '' // Changed from mobileNumber to match API field name
  });
  const [errors, setErrors] = useState({});
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitStatus, setSubmitStatus] = useState({ success: false, message: '' });
  const [isFormInvalid, setIsFormInvalid] = useState(true);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prevState => ({
      ...prevState,
      [name]: value
    }));
  };

  const validateField = (name, value) => {
    let errorMessage = '';
    switch (name) {
      case 'name':
        if (!value.match(/^[a-zA-Z\s]{3,}$/)) {
          errorMessage = 'Please enter a valid name (3+ letters).';
        }
        break;
      case 'email':
        if (!value.match(/^[^\s@]+@[^\s@]+\.[^\s@]+$/)) {
          errorMessage = 'Please enter a valid email.';
        }
        break;
      case 'phone': // Changed from mobileNumber to phone
        if (value && !value.match(/^\+?\d{10,15}$/)) {
          errorMessage = 'Please enter a valid phone number (10-15 digits, optional +).';
        }
        break;
      default:
        break;
    }
    return errorMessage;
  };

  // Validate form on every change to update button state
  useEffect(() => {
    const newErrors = {};
    let hasErrors = false;

    // Check if required fields are filled
    if (!formData.name || !formData.email) {
      hasErrors = true;
    } else {
      // Validate each field
      ['name', 'email', 'phone'].forEach((key) => {
        const error = validateField(key, formData[key]);
        if (error) {
          newErrors[key] = error;
          hasErrors = true;
        }
      });
    }

    setIsFormInvalid(hasErrors);
  }, [formData]);

  const handleBlur = (e) => {
    const { name, value } = e.target;
    const error = validateField(name, value);
    setErrors((prevErrors) => ({ ...prevErrors, [name]: error }));
  };

  const dismissError = (field) => {
    setErrors((prevErrors) => ({ ...prevErrors, [field]: null }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    // Validate all fields
    const newErrors = {};
    ['name', 'email', 'phone'].forEach((key) => {
      const error = validateField(key, formData[key]);
      if (error) newErrors[key] = error;
    });
    if (Object.keys(newErrors).length > 0) {
      setErrors(newErrors);
      setIsFormInvalid(true);
      return;
    }

    setIsSubmitting(true);
    try {
      // Call the API function with the form data
      const response = await submitDownloadEbookForm(formData);

      if (response && response.message) {
        setSubmitStatus({
          success: true,
          message: 'Form submitted successfully! Download will start soon.'
        });

        // Reset form after successful submission
        setFormData({ name: '', email: '', phone: '' });
        setErrors({});
        setIsFormInvalid(true);

        // You could trigger the download here if needed
        // For example: window.open('/path-to-ebook.pdf', '_blank');
      } else {
        throw new Error('Unexpected response format');
      }
    } catch (error) {
      console.error('Form submission error:', error);
      setSubmitStatus({
        success: false,
        message: error.message || 'Failed to submit form. Please try again.'
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div className={styles.disclaimerContainer}>
      <div className={styles.disclaimer}>
        <h3 className={styles.disclaimerTitle}>{t('Disclaimer:')}</h3>
        <p className={styles.disclaimerText}>
          {t('The information provided in this blog is for educational purposes only and should not be considered financial advice. Always consult with a professional financial advisor or lender for specific lending decisions.')}
        </p>
      </div>

      <div className={styles.ebookContainer}>
        <h2 className={styles.ebookTitle}>{t('Download Your Free E-Book Today!')}</h2>
        <p className={styles.ebookDescription}>
          {t('Unlock instant access to our comprehensive E-Book – packed with insights and strategies you won\'t want to miss. Simply share a few details to begin your journey:')}
        </p>

        <form onSubmit={handleSubmit} className={styles.ebookForm}>
          <div className={styles.formField}>
            {errors.name && (
              <div className={styles.errorOverlay}>
                {errors.name}
                <button
                  className={styles.dismissButton}
                  onClick={() => dismissError('name')}
                  aria-label="Dismiss name error"
                  type="button"
                >
                  ✕
                </button>
              </div>
            )}
            {!errors.name && (
              <input
                type="text"
                id="name"
                name="name"
                value={formData.name}
                onChange={handleChange}
                onBlur={handleBlur}
                placeholder={t('Name*')}
                required
                className={styles.inputField}
                aria-invalid={!!errors.name}
              />
            )}
          </div>

          <div className={styles.formField}>
            {errors.email && (
              <div className={styles.errorOverlay}>
                {errors.email}
                <button
                  className={styles.dismissButton}
                  onClick={() => dismissError('email')}
                  aria-label="Dismiss email error"
                  type="button"
                >
                  ✕
                </button>
              </div>
            )}
            {!errors.email && (
              <input
                type="email"
                id="email"
                name="email"
                value={formData.email}
                onChange={handleChange}
                onBlur={handleBlur}
                placeholder={t('Email*')}
                required
                className={styles.inputField}
                aria-invalid={!!errors.email}
              />
            )}
          </div>

          <div className={styles.formField}>
            {errors.phone && (
              <div className={styles.errorOverlay}>
                {errors.phone}
                <button
                  className={styles.dismissButton}
                  onClick={() => dismissError('phone')}
                  aria-label="Dismiss phone number error"
                  type="button"
                >
                  ✕
                </button>
              </div>
            )}
            {!errors.phone && (
              <input
                type="tel"
                id="phone"
                name="phone" // Changed from mobileNumber to phone
                value={formData.phone}
                onChange={handleChange}
                onBlur={handleBlur}
                placeholder={t('Mobile Number')}
                className={styles.inputField}
                aria-invalid={!!errors.phone}
              />
            )}
          </div>

          <button
            type="submit"
            className={styles.submitButton}
            disabled={isSubmitting || isFormInvalid}
            aria-disabled={isSubmitting || isFormInvalid}
            aria-label={isSubmitting ? t("Submitting...") : t("Secure my free e-book")}
          >
            {isSubmitting ? t("Submitting...") : t("Secure my free e-book")}
          </button>

          {submitStatus.message && (
            <div className={`${styles.submitStatus} ${submitStatus.success ? styles.success : styles.error}`}>
              {submitStatus.message}
            </div>
          )}
        </form>
      </div>
    </div>
  );
};

export default Disclaimer;
